import React, { useEffect, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Chart, BarElement, BarController,
  CategoryScale, LinearScale, Tooltip,
} from "chart.js";

Chart.register(BarElement, BarController, CategoryScale, LinearScale, Tooltip);

const FteByMac = ({ fteByMac }) => {
  const theme     = useTheme();
  const t         = theme.custom;
  const isDark    = theme.palette.mode === "dark";
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  useEffect(() => {
    if (!fteByMac || fteByMac.length === 0 || !canvasRef.current) return;

    if (chartRef.current) chartRef.current.destroy();

    const labels = fteByMac.map(r => r.macCode);
    const values = fteByMac.map(r => Number(r.totalFte));

    // gold gradient per bar — same color, varying opacity by rank
    const bgColors = fteByMac.map((_, i) => {
      const opacity = isDark
        ? 1 - (i / fteByMac.length) * 0.55
        : 1 - (i / fteByMac.length) * 0.5;
      return isDark
        ? `rgba(214,223,39,${opacity})`
        : `rgba(214,223,39,${opacity})`;
    });

    chartRef.current = new Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels,
        datasets: [{
          data: values,
          backgroundColor: bgColors,
          borderRadius: 6,
          borderSkipped: false,
          barThickness: 18,
        }],
      },
      options: {
        indexAxis: "y",           // horizontal bars
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: t.surface2,
            borderColor: t.border2,
            borderWidth: 1,
            titleColor: t.text,
            bodyColor: t.text2,
            padding: 10,
            callbacks: {
              title: (items) => {
                const idx = items[0].dataIndex;
                return fteByMac[idx].macName;
              },
              label: (ctx) => `  ${Number(ctx.parsed.x).toFixed(1)} FTE`,
            },
          },
        },
        scales: {
          x: {
            grid: {
              color: isDark ? "rgba(255,255,255,0.04)" : "rgba(3,81,77,0.06)",
            },
            border: { display: false },
            ticks: {
              color: t.text3,
              font: { size: 10 },
            },
          },
          y: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              color: t.text2,
              font: { size: 11, weight: "600" },
            },
          },
        },
      },
    });

    return () => { chartRef.current?.destroy(); };
  }, [fteByMac, isDark]);

  if (!fteByMac || fteByMac.length === 0) return null;

  const total = fteByMac.reduce((s, r) => s + Number(r.totalFte), 0);
  const chartHeight = Math.max(160, fteByMac.length * 38);

  return (
    <div style={{
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: 12,
      overflow: "hidden",
    }}>

      {/* header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 18px",
        borderBottom: `1px solid ${t.border}`,
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>FTE by MAC</span>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: "2px 8px",
          borderRadius: 20,
          background: isDark ? "rgba(214,223,39,0.18)" : "rgba(214,223,39,0.14)",
          color: isDark ? "#D6DF27" : "#6B7300",
        }}>
          {Number(total).toFixed(1)} total
        </span>
      </div>

      {/* chart */}
      <div style={{ padding: "16px 18px" }}>
        <div style={{ position: "relative", height: chartHeight }}>
          <canvas ref={canvasRef} />
        </div>
      </div>

      {/* MAC name legend below */}
      <div style={{
        display: "flex", flexDirection: "column", gap: 0,
        borderTop: `1px solid ${t.border}`,
      }}>
        {fteByMac.map((row, i) => {
          const pct = Math.round((Number(row.totalFte) / (total || 1)) * 100);
          const opacity = 1 - (i / fteByMac.length) * 0.5;
          return (
            <div key={row.macCode} style={{
              display: "flex", alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 18px",
              borderBottom: i < fteByMac.length - 1 ? `1px solid ${t.border}` : "none",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{
                  width: 8, height: 8, borderRadius: 2, flexShrink: 0,
                  background: `rgba(214,223,39,${opacity})`,
                }} />
                <span style={{ fontSize: 11, color: t.text2 }}>{row.macName}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: t.text }}>
                  {Number(row.totalFte).toFixed(1)}
                </span>
                <span style={{
                  fontSize: 10, color: isDark ? "#6B7300" : t.text3,
                  color: isDark ? "#D6DF27" : "#6B7300",
                  fontWeight: 600,
                }}>
                  {pct}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FteByMac;